import { useQuery } from "@tanstack/react-query";

import { toApiError } from "../../lib/apiError";
import type { ApiClient } from "../client";
import { useApiClient } from "../useApiClient";

export const TRADES_QUERY_KEY = ["trades"] as const;

// Shared query definition (key + fetcher) so the same query can be driven from
// the component hook AND warmed imperatively for nav prefetch (see
// useNavPrefetch) without duplicating the endpoint/error wiring.
export function tradesQueryOptions(client: ApiClient, params: ListTradesQuery = {}) {
  return {
    queryKey: [...TRADES_QUERY_KEY, params],
    queryFn: async () => {
      const { data, error } = await client.GET("/api/trades", {
        params: { query: params },
      });
      if (error !== undefined) {
        throw new Error(toApiError(error, "Failed to list trades"));
      }
      return data;
    },
  };
}

// Two queries belong to the "same filter set" when they narrow to the same
// rows -- i.e. only pagination (limit/offset) or sort differs. Keeping the
// previous page's rows visible across those changes makes paging feel smooth;
// a screen_id or date-window change is a different set, so we DON'T keep the
// old rows (that would flash the prior filter's data before the new one loads).
function isSameTradeFilter(a: ListTradesQuery, b: ListTradesQuery): boolean {
  return (
    a.screen_id === b.screen_id &&
    a.opened_after === b.opened_after &&
    a.opened_before === b.opened_before &&
    a.symbol_query === b.symbol_query
  );
}

export type TradeSortKey = "created_at" | "updated_at" | "opened_at" | "closed_at";
export type TradeSortOrder = "asc" | "desc";

export type ListTradesQuery = {
  limit?: number;
  offset?: number;
  screen_id?: string;
  sort_key?: TradeSortKey;
  sort_order?: TradeSortOrder;
  opened_after?: string;
  opened_before?: string;
  symbol_query?: string;
};

export function useTrades(params: ListTradesQuery = {}, enabled = true) {
  const client = useApiClient();
  return useQuery({
    ...tradesQueryOptions(client, params),
    enabled,
    placeholderData: (previousData, previousQuery) => {
      if (previousData === undefined || previousQuery === undefined) {
        return undefined;
      }
      const previousParams = previousQuery.queryKey[1] as
        | ListTradesQuery
        | undefined;
      if (previousParams === undefined) {
        return undefined;
      }
      return isSameTradeFilter(previousParams, params) ? previousData : undefined;
    },
  });
}
